import * as React from 'react'
import { cn } from '@/lib/utils'

/* Design v2 surface primitives: Paper, Tape, Tag.
   Look lives in styles/design-v2.css (@layer components, scoped to [data-design="v2"]),
   so Tailwind utilities passed via className still win. */

export type TapePosition = 'top' | 'top-left' | 'top-right'

export interface TapeProps extends React.HTMLAttributes<HTMLSpanElement> {
  position?: TapePosition
}

/** A strip of translucent tape. Place inside a `relative` parent (Paper/Tag do this for you). */
export function Tape({ position = 'top', className, ...props }: TapeProps) {
  return <span aria-hidden="true" data-position={position} className={cn('v2-tape', className)} {...props} />
}

export interface PaperProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Faint 28px ruled lines. Opt-in: keep off for dense lists. */
  ruled?: boolean
  /** Tape the sheet to the table. Pass `true` for centred, or a position. */
  tape?: boolean | TapePosition | TapePosition[]
  /** Small tilt in degrees (e.g. -1). Uses the `rotate` property so transforms still compose. */
  tilt?: number
  as?: 'div' | 'section' | 'article' | 'aside' | 'li'
}

/** A paper sheet lying on the table. */
export const Paper = React.forwardRef<HTMLDivElement, PaperProps>(function Paper(
  { ruled = false, tape, tilt, as = 'div', className, style, children, ...props },
  ref,
) {
  const Comp = as as 'div'
  const tapes: TapePosition[] =
    tape === true ? ['top'] : typeof tape === 'string' ? [tape] : Array.isArray(tape) ? tape : []
  return (
    <Comp
      ref={ref}
      data-ruled={ruled || undefined}
      className={cn('v2-paper relative', className)}
      style={tilt ? { rotate: `${tilt}deg`, ...style } : style}
      {...props}
    >
      {tapes.map((p) => (
        <Tape key={p} position={p} />
      ))}
      {children}
    </Comp>
  )
})

export interface TagProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tilt in degrees. Default -1.2 (Dura). Pass 0 to sit straight. */
  tilt?: number
  /** Tape the tag at the top centre. Default true. */
  taped?: boolean
}

/** A slightly tilted paper tag (labels, daily themes, prices pinned to the table). */
export function Tag({ tilt = -1.2, taped = true, className, style, children, ...props }: TagProps) {
  return (
    <div className={cn('v2-tag relative', className)} style={{ rotate: `${tilt}deg`, ...style }} {...props}>
      {taped && <Tape position="top" className="v2-tape-sm" />}
      {children}
    </div>
  )
}
